import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import { AnalyticsService, ClientAnalyticsService, CompanyService } from '$/services';
import * as Bixby from '$/core';
import { PortfolioGrid } from '$/components/portfolio-grid';
import {
  getIsCurrentUserAdminState,
  getIsCurrentUserCreditSightsAnalystState,
  getIsCurrentUserCreditSightsOpsState,
  getIsCurrentUserRatingsAnalystState,
  getIsCurrentUserSuFAnalystState,
} from '$/store/profile/selectors';
import Loading from '$/components/loading/Loading';
import { TransactionCompsGrid } from '$/components/transaction-comps-grid';
import IndustryAnalytics from '$/pages/client/company/CompanyComps/IndustryAnalytics';
import FilterButtons, { IFilterButton } from '$/components/filterButtons';
import { getPublicCompaniesFeatureFlagState } from '$/store/release-feature-flags/selectors';
import { CLIENT_ANALYTICS_VIEW, CLIENT_COMPANY_COMPS } from '$/core/constants';
import './index.scss';
import HeadingBlock, { IToggle } from '$/components/global/heading';
import BixToggle from '$/components/global/bixToggle';
import {
  getHasBixbyComplianceFeatureFlagState,
  getHasPublicCompaniesFeatureFlagState,
  getHasUsEuroGroupingFeatureFlagState,
} from '$/store/login/selectors';
import NotFound from '$/pages/not-found';
import HeatMapKey from '$/components/heatMapKey';
import { GridApi } from 'ag-grid-enterprise';

interface ICompanyCompsProps {
  currentCompany?: Bixby.Models.Company;
  isAdmin: boolean;
  isCreditSightsOpsSufOrRatings: boolean;
  isPublicCompaniesEnabled: boolean;
  hasPublicCompaniesFeatureFlag: boolean;
  featureFlags?: string[];
  hasBixbyComplianceFeatureFlag: boolean;
  profile?: Bixby.Models.Profile;
  hasUsEuroGroupingFeatureFlag: boolean;
}

const PUBLIC_COMPANIES_LABEL = 'Public Companies';
const ALL_COMPANIES_LABEL = 'All Companies';
const PRIVATE_COMPANIES_LABEL = 'Private Companies';

const CompanyComps: React.FC<ICompanyCompsProps> = props => {
  const {
    currentCompany,
    isAdmin,
    isCreditSightsOpsSufOrRatings,
    isPublicCompaniesEnabled,
    featureFlags,
    hasPublicCompaniesFeatureFlag,
    hasBixbyComplianceFeatureFlag,
    profile,
    hasUsEuroGroupingFeatureFlag,
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [companyCompError, setCompanyCompsError] = useState(undefined as string | undefined);
  const [transactionCompError, setTansactionCompsError] = useState(undefined as string | undefined);
  const [industryAnalyticsError, setIndustryAnalyticsError] = useState(undefined as string | undefined);
  const [currentTransactionComp, setCurrentTransactionComp] = useState([] as Bixby.Models.CompanyTransactionComp[]);
  const [currentCompanyProfolio, setcurrentCompanyProfolio] = useState([] as Bixby.Models.CompanyPortfolio[]);
  const [marketAnalytics, setmarketAnalytics] = useState(undefined as Bixby.Models.CompanyAnalyticsResult | undefined);
  const [portfolioComps, setPortfolioComps] = useState([] as Bixby.Models.CompanyPortfolio[]);
  const [transactionComps, setTransactionComps] = useState([] as Bixby.Models.CompanyTransactionComp[]);
  const [industryAnalytics, setindustryAnalytics] = useState(
    undefined as Bixby.Models.CompanyAnalyticsResult | undefined
  );
  const isAdminOrCreditSightsOpsSufOrRatings = isAdmin || isCreditSightsOpsSufOrRatings;
  const [heatMapActive, setHeatMapActive] = useState(true as boolean);
  const [industryHeatMapActive, setIndustryHeatMapActive] = useState(false as boolean);
  const [allCompaniesActive, setAllCompaniesActive] = useState(false as boolean);
  const [privateCompaniesActive, setPrivateCompaniesActive] = useState(!currentCompany?.isSECPublic as boolean);
  const [publicCompaniesActive, setPublicCompaniesActive] = useState(currentCompany?.isSECPublic as boolean);
  const [gridView, setGridView] = useState(Bixby.Constants.ANALYTICS_PORTFOLIO as string);
  const [companyDataGridRef, setCompanyDataGridRef] = useState(null as GridApi | null);
  const [companyCompsGridRef, setCompanyCompsGridRef] = useState(null as GridApi | null);
  const [transactionDataGridRef, setTransactionDataGridRef] = useState(null as GridApi | null);
  const [transactionCompsGridRef, setTransactionCompsGridRef] = useState(null as GridApi | null);
  const [viewFilters, setViewFilters] = useState([
    { id: Bixby.Constants.ANALYTICS_PORTFOLIO, name: 'Fundamental Comps', selected: true },
    { id: Bixby.Constants.ANALYTICS_TRANSACTION_COMPS, name: 'Transaction Comps', selected: false },
  ] as IFilterButton[]);

  const companyView = useMemo(() => {
    if (allCompaniesActive) {
      return ALL_COMPANIES_LABEL;
    } else if (privateCompaniesActive) {
      return PRIVATE_COMPANIES_LABEL;
    } else if (publicCompaniesActive) {
      return PUBLIC_COMPANIES_LABEL;
    }
  }, [allCompaniesActive, publicCompaniesActive, privateCompaniesActive]);

  const heatMapToggle: IToggle | undefined = {
    checked: heatMapActive,
    onChange: (isHeatMapOn: boolean) => {
      if (isHeatMapOn) {
        setHeatMapActive(false);
      } else {
        if (industryHeatMapActive) {
          setIndustryHeatMapActive(false);
        }
        setHeatMapActive(true);
      }
    },
  };

  const industryHeatMapToggle: IToggle | undefined = {
    checked: industryHeatMapActive,
    onChange: (isHeatMapOn: boolean) => {
      if (isHeatMapOn) {
        setIndustryHeatMapActive(false);
      } else {
        if (heatMapActive) {
          setHeatMapActive(false);
        }
        setIndustryHeatMapActive(true);
      }
    },
  };

  const allCompaniesToggle: IToggle | undefined = {
    checked: allCompaniesActive,
    onChange: (isAllCompaniesOn: boolean) => {
      if (!isAllCompaniesOn) {
        if (privateCompaniesActive || publicCompaniesActive) {
          setPrivateCompaniesActive(false);
          setPublicCompaniesActive(false);
        }
        setAllCompaniesActive(true);
      }
    },
  };

  const privateCompaniesToggle: IToggle | undefined = {
    checked: privateCompaniesActive,
    onChange: (isPrivateCompaniesOn: boolean) => {
      if (!isPrivateCompaniesOn) {
        if (allCompaniesActive || publicCompaniesActive) {
          setAllCompaniesActive(false);
          setPublicCompaniesActive(false);
        }
        setPrivateCompaniesActive(true);
      }
    },
  };

  const publicCompaniesToggle: IToggle | undefined = {
    checked: publicCompaniesActive,
    onChange: (isPublicCompaniesOn: boolean) => {
      if (!isPublicCompaniesOn) {
        if (allCompaniesActive || privateCompaniesActive) {
          setAllCompaniesActive(false);
          setPrivateCompaniesActive(false);
        }
        setPublicCompaniesActive(true);
      }
    },
  };

  useEffect(() => {
    const getCompanyComps = async () => {
      if (!currentCompany) return;

      setIsLoading(true);
      setCompanyCompsError(undefined);
      try {
        if (currentCompany && currentCompany.industry_Group_Id !== null) {
          const result: Bixby.Models.CompanyPortfolio[] = await CompanyService.getComps(
            currentCompany.industry_Group_Id
          );

          setcurrentCompanyProfolio(result?.filter(_ => _.companyId === currentCompany.id));
          if (!isPublicCompaniesEnabled) {
            setPortfolioComps(result?.filter(_ => !_.isSECPublic));
          } else {
            setPortfolioComps(result);
          }
        }
      } catch (e: any) {
        setCompanyCompsError('Error retrieving company comps');
        setPortfolioComps([]);
        setcurrentCompanyProfolio([]);
      } finally {
        setIsLoading(false);
      }
    };

    getCompanyComps();
  }, [currentCompany, isPublicCompaniesEnabled]);

  useEffect(() => {
    const getTransactionComps = async () => {
      if (!currentCompany) return;

      setIsLoading(true);
      setTansactionCompsError(undefined);
      try {
        if (currentCompany && currentCompany.industry_Group_Id !== null) {
          const result: Bixby.Models.CompanyTransactionComp[] = await CompanyService.getTransactionCompsByIndustryGroup(
            currentCompany.industry_Group_Id
          );
          setTransactionComps(result);
          setCurrentTransactionComp(result?.filter(_ => _.companyId === currentCompany.id));
        }
      } catch (e: any) {
        setTansactionCompsError('Error retrieving transaction comps');
        setCurrentTransactionComp([]);
        setTransactionComps([]);
      } finally {
        setIsLoading(false);
      }
    };

    getTransactionComps();
  }, [currentCompany]);

  useEffect(() => {
    async function getIndustryAnalytics() {
      setIsLoading(true);
      setIndustryAnalyticsError(undefined);
      try {
        if (currentCompany && currentCompany.industry_Group_Id !== null) {
          const analyticsResults: Bixby.Models.CompanyAnalyticsResult =
            await ClientAnalyticsService.getIndustryGroupAnalytics(currentCompany.industry_Group_Id);
          setindustryAnalytics(analyticsResults || []);
        }
      } catch (e: any) {
        setIndustryAnalyticsError('Error retrieving analytics');
        setindustryAnalytics(undefined);
      } finally {
        setIsLoading(false);
      }
    }

    if (currentCompany) {
      getIndustryAnalytics();
    }
  }, [currentCompany, isPublicCompaniesEnabled]);

  useEffect(() => {
    async function getMarketAnalytics() {
      const result: Bixby.Models.CompanyAnalyticsResult = await ClientAnalyticsService.getMarketDataAnalytics();
      setmarketAnalytics(result);
    }
    getMarketAnalytics();
  }, []);

  const currentCompanyAnalytics: Bixby.Models.CompanyAnalytics[] | [] = useMemo(() => {
    if (!currentCompanyProfolio || currentCompanyProfolio.length === 0) return [];
    const transactionCompExists = currentTransactionComp && currentTransactionComp.length > 0;
    return [
      {
        name: [currentCompanyProfolio[0].uniqueName],
        type: '',
        firstLienDebtGrossEbitda: currentCompanyProfolio[0].firstLienDebtGrossEbitda,
        totalDebtGrossEbitda: currentCompanyProfolio[0].totalDebtGrossEbitda,
        firstLienDebtGrossAdjustedEbitda: currentCompanyProfolio[0].firstLienDebtGrossAdjustedEbitda,
        totalDebtGrossAdjustedEbitda: currentCompanyProfolio[0].totalDebtGrossAdjustedEbitda,
        interestCoverageEbitda: currentCompanyProfolio[0].interestCoverageEbitda,
        interestCoverageAdjustedEbitda: currentCompanyProfolio[0].interestCoverageAdjustedEbitda,
        interestCoverageAfterCapitalExpendituresEbitda:
          currentCompanyProfolio[0].interestCoverageAfterCapitalExpendituresEbitda,
        interestCoverageAfterCapitalExpendituresAdjustedEbitda:
          currentCompanyProfolio[0].interestCoverageAfterCapitalExpendituresAdjustedEbitda,
        liquidity: null,
        revenueGrowth: currentCompanyProfolio[0].revenueGrowth,
        ebitdaGrowth: currentCompanyProfolio[0].ebitdaGrowth,
        adjustedEbitdaGrowth: currentCompanyProfolio[0].adjustedEbitdaGrowth,
        ebitdaMargin: currentCompanyProfolio[0].ebitdaMargin,
        adjustedEbitdaMargin: currentCompanyProfolio[0].adjustedEbitdaMargin,
        ocfMargin: currentCompanyProfolio[0].ocfMargin,
        capitalExpendituresMargin: currentCompanyProfolio[0].capitalExpendituresMargin,
        firstLien1Amount: currentCompanyProfolio[0].firstLien1Amount,
        firstLien1Spread: Bixby.Utils.formatSpread(currentCompanyProfolio[0].firstLien1Spread),
        totalEnterpriseValue: transactionCompExists ? currentTransactionComp[0].totalEnterpriseValue : null,
        firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda: null,
        totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda: null,
        totalEnterpriseValueByRevenue: transactionCompExists
          ? currentTransactionComp[0].totalEnterpriseValueByRevenue
          : null,
        totalEnterpriseValueByAdjustedManagementEbitda: transactionCompExists
          ? currentTransactionComp[0].totalEnterpriseValueByAdjustedManagementEbitda
          : null,
        revenueAtBuyout: transactionCompExists ? currentTransactionComp[0].revenueAtBuyout : null,
        managementAdjustedEbitdaAtBuyout: transactionCompExists
          ? currentTransactionComp[0].managementAdjustedEbitdaAtBuyout
          : null,
        netDebtPercentageOfCap: transactionCompExists ? currentTransactionComp[0].netDebtPercentageOfCap : null,
        totalEquityPercentageOfCap: transactionCompExists ? currentTransactionComp[0].totalEquityPercentageOfCap : null,
        cumulativeDividendsByImpliedEquityAtRisk: null,
      },
    ];
  }, [currentCompanyProfolio, currentTransactionComp]);

  const filteredIndustryAnalytics = useMemo(() => {
    if (!industryAnalytics) return industryAnalytics;

    if (companyView === PUBLIC_COMPANIES_LABEL) {
      return [...currentCompanyAnalytics, ...industryAnalytics.publicCompanies];
    } else if (companyView === PRIVATE_COMPANIES_LABEL) {
      return [...currentCompanyAnalytics, ...industryAnalytics.privateCompanies];
    } else if (companyView === ALL_COMPANIES_LABEL) {
      return [...currentCompanyAnalytics, ...industryAnalytics.allCompanies];
    }
  }, [companyView, currentCompanyAnalytics, industryAnalytics]);

  const filteredPortfolioComps = useMemo(() => {
    if (!portfolioComps) return portfolioComps;

    if (companyView === PUBLIC_COMPANIES_LABEL) {
      return portfolioComps.filter(_ => _.isSECPublic);
    } else if (companyView === PRIVATE_COMPANIES_LABEL) {
      return portfolioComps.filter(_ => !_.isSECPublic);
    } else if (companyView === ALL_COMPANIES_LABEL) {
      return portfolioComps;
    }
  }, [companyView, portfolioComps]);

  const filterdMarketAnalytics = useMemo(() => {
    if (!marketAnalytics) return marketAnalytics;

    if (companyView === PUBLIC_COMPANIES_LABEL) {
      return marketAnalytics.publicCompanies;
    } else if (companyView === PRIVATE_COMPANIES_LABEL) {
      return marketAnalytics.privateCompanies;
    } else if (companyView === ALL_COMPANIES_LABEL) {
      return marketAnalytics.allCompanies;
    }
  }, [marketAnalytics, companyView]);

  const onSetGridView = (id: string, futureState: boolean) => {
    setViewFilters(viewFilters.map(_ => ({ ..._, selected: _.id === id ? futureState : false })));
    setGridView(id);
    //disable toggles and set to private for transaction comps
    if (id == Bixby.Constants.ANALYTICS_TRANSACTION_COMPS) {
      allCompaniesToggle.onChange(true);
      privateCompaniesToggle.onChange(false);
      publicCompaniesToggle.onChange(true);
    }
  };

  let compsFeedbackUrl = '';

  if (currentCompany) {
    const companyNameSanitized: string = (currentCompany.name || '').replace('&', '%26');
    const feedbackUrlSubject = `Hi%20-%20I%20would%20like%20to%20inquire%20about%20analytics%20for%20${companyNameSanitized}`;
    compsFeedbackUrl = `mailto:feedback@bixbyresearch.com?subject=${feedbackUrlSubject}`;
  }

  const handleFeedbackClick = () => {
    if (profile && currentCompany) {
      AnalyticsService.event(Bixby.Constants.APPINSIGHTS_FEEDBACK_LINK_CLICK, {
        ...profile,
        type: 'Analytics',
        companyName: currentCompany.name,
      });
    }
  };

  if (!featureFlags?.includes(CLIENT_COMPANY_COMPS)) {
    return <NotFound />;
  }
  if (!featureFlags?.includes(CLIENT_ANALYTICS_VIEW)) {
    return (
      <div className="analytics_not_enabled">
        {' '}
        You are not currently enabled for Analytics, please{' '}
        <a href={compsFeedbackUrl} role="button" onClick={handleFeedbackClick}>
          click here
        </a>{' '}
        to contact Bixby to have this feature enabled.
      </div>
    );
  }
  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <>
        <div className="heading_block">
          <div className="top_wrap">
            <FilterButtons filters={viewFilters} onFilter={onSetGridView} />
            <div className="top_options">
              <div className="toggle-icons">
                <p className="heat_map_label">Apply benchmarking / heat maps</p>
                <div
                  className={
                    'toggle_columns ' + (gridView === Bixby.Constants.ANALYTICS_TRANSACTION_COMPS ? 'fade' : '')
                  }>
                  <div className="companies_toggle">
                    <BixToggle
                      id="all_companies_toggle"
                      checked={allCompaniesToggle.checked}
                      text="vs. All Companies"
                      onChange={l => allCompaniesToggle.onChange(!l)}
                      disabled={gridView === Bixby.Constants.ANALYTICS_TRANSACTION_COMPS}
                    />
                  </div>
                  <div className="companies_toggle">
                    <BixToggle
                      id="private_companies_toggle"
                      checked={privateCompaniesToggle.checked}
                      text="vs. Private Companies"
                      onChange={l => privateCompaniesToggle.onChange(!l)}
                      disabled={gridView === Bixby.Constants.ANALYTICS_TRANSACTION_COMPS}
                    />
                  </div>
                  <div className="companies_toggle">
                    <BixToggle
                      id="public_companies_toggle"
                      checked={publicCompaniesToggle.checked}
                      text="vs. Public Companies"
                      onChange={l => publicCompaniesToggle.onChange(!l)}
                      disabled={gridView === Bixby.Constants.ANALYTICS_TRANSACTION_COMPS}
                    />
                  </div>
                </div>
                <div className="vertical_line"></div>
                <div className="toggle_columns">
                  <div className="heat_map_note">
                    <BixToggle
                      id="heat_map_toggle"
                      checked={heatMapToggle.checked}
                      text="vs. All Loan Market"
                      onChange={l => heatMapToggle.onChange(!l)}
                    />
                  </div>
                  <div className="heat_map_note">
                    <BixToggle
                      id="industry_heat_map_toggle"
                      checked={industryHeatMapToggle.checked}
                      text="vs. Industry Group"
                      onChange={l => industryHeatMapToggle.onChange(!l)}
                    />
                  </div>
                </div>
                <p className="note">
                  {' '}
                  {heatMapActive || industryHeatMapActive ? '*Heat map is benchmarking vs ' : '*Benchmarking vs '}{' '}
                  {companyView?.toLowerCase()}{' '}
                  {industryHeatMapActive
                    ? " in the issuer's industry group."
                    : heatMapActive
                    ? ' in the entire loan market.'
                    : '.'}
                </p>
              </div>
              <HeatMapKey />
            </div>
          </div>
        </div>
        <HeadingBlock title="Company Data" />
        {gridView && gridView == Bixby.Constants.ANALYTICS_PORTFOLIO && (
          <div className="company_data_grid portfolio_data_grid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                {!Bixby.Utils.isNullOrEmpty(companyCompError) ? (
                  <p className="error">{companyCompError}</p>
                ) : (
                  <PortfolioGrid
                    gridData={currentCompanyProfolio}
                    marketAnalytics={heatMapActive ? filterdMarketAnalytics : filteredIndustryAnalytics}
                    isAdminOrCreditSightsOpsSufOrRatings={isAdminOrCreditSightsOpsSufOrRatings}
                    hasPublicCompaniesFeatureFlag={hasPublicCompaniesFeatureFlag}
                    hasBixbyComplianceFeatureFlag={hasBixbyComplianceFeatureFlag}
                    hasUsEuroGroupingFeatureFlag={hasUsEuroGroupingFeatureFlag}
                    heatMapActive={heatMapActive ? heatMapActive : industryHeatMapActive}
                    setGridRef={setCompanyDataGridRef}
                    linkedGrid={companyCompsGridRef}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        {gridView && gridView == Bixby.Constants.ANALYTICS_TRANSACTION_COMPS && (
          <section className="transaction_comps">
            <div className="company_transaction_data_grid transaction_comps_data_grid">
              {!Bixby.Utils.isNullOrEmpty(transactionCompError) ? (
                <p className="error">{transactionCompError}</p>
              ) : currentTransactionComp && !Bixby.Utils.isNullOrEmpty(currentTransactionComp) ? (
                <TransactionCompsGrid
                  gridData={currentTransactionComp}
                  isAdmin={isAdmin}
                  setGridRef={setTransactionDataGridRef}
                  linkedGrid={transactionCompsGridRef}
                />
              ) : (
                <p className="buyout_details"> No buyout details for {currentCompany?.name} </p>
              )}
            </div>
          </section>
        )}
        <HeadingBlock title={`Market Benchmarks (${companyView ? companyView : 'NA'})`}></HeadingBlock>
        {!Bixby.Utils.isNullOrEmpty(industryAnalyticsError) ? (
          <p className="error">{industryAnalyticsError}</p>
        ) : (
          <IndustryAnalytics
            dataForGrid={filteredIndustryAnalytics}
            marketAnalytics={heatMapActive ? filterdMarketAnalytics : filteredIndustryAnalytics}
            heatMapActive={heatMapActive}
            IsIndustryMapping={industryHeatMapActive}
            gridView={gridView}
          />
        )}

        {gridView && gridView == Bixby.Constants.ANALYTICS_PORTFOLIO && (
          <>
            <HeadingBlock title="Comparable Companies" />
            <div className="portfolio_data_grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  {!Bixby.Utils.isNullOrEmpty(companyCompError) ? (
                    <p className="error">{companyCompError}</p>
                  ) : (
                    <PortfolioGrid
                      gridData={filteredPortfolioComps}
                      marketAnalytics={heatMapActive ? filterdMarketAnalytics : filteredIndustryAnalytics}
                      isAdminOrCreditSightsOpsSufOrRatings={isAdminOrCreditSightsOpsSufOrRatings}
                      hasPublicCompaniesFeatureFlag={hasPublicCompaniesFeatureFlag}
                      hasBixbyComplianceFeatureFlag={hasBixbyComplianceFeatureFlag}
                      hasUsEuroGroupingFeatureFlag={hasUsEuroGroupingFeatureFlag}
                      heatMapActive={heatMapActive ? heatMapActive : industryHeatMapActive}
                      setGridRef={setCompanyCompsGridRef}
                      linkedGrid={companyDataGridRef}
                    />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        {gridView && gridView == Bixby.Constants.ANALYTICS_TRANSACTION_COMPS && (
          <section className="transaction_comps">
            <HeadingBlock title="Transaction Comps" />
            <div className="transaction_comps_data_grid">
              {!Bixby.Utils.isNullOrEmpty(transactionCompError) ? (
                <p className="error">{transactionCompError}</p>
              ) : (
                <TransactionCompsGrid
                  gridData={transactionComps}
                  isAdmin={isAdmin}
                  setGridRef={setTransactionCompsGridRef}
                  linkedGrid={transactionDataGridRef}
                />
              )}
            </div>
          </section>
        )}
      </>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { isCreditSightsAnalyst } = getIsCurrentUserCreditSightsAnalystState(state);
  const { isRatingsAnalyst } = getIsCurrentUserRatingsAnalystState(state);
  const { isSuFAnalyst } = getIsCurrentUserSuFAnalystState(state);
  const { isCreditSightsOps } = getIsCurrentUserCreditSightsOpsState(state);
  const { isAdmin } = getIsCurrentUserAdminState(state);
  const { isPublicCompaniesEnabled } = getPublicCompaniesFeatureFlagState(state);
  const { hasPublicCompaniesFeatureFlag } = getHasPublicCompaniesFeatureFlagState(state);
  const { hasBixbyComplianceFeatureFlag } = getHasBixbyComplianceFeatureFlagState(state);
  const { hasUsEuroGroupingFeatureFlag } = getHasUsEuroGroupingFeatureFlagState(state);

  return {
    currentCompany: state.portfolioState.currentCompany,
    isAdmin,
    isCreditSightsOpsSufOrRatings: isCreditSightsAnalyst || isCreditSightsOps || isSuFAnalyst || isRatingsAnalyst,
    featureFlags: state.loginState.featureFlags,
    isPublicCompaniesEnabled,
    hasBixbyComplianceFeatureFlag: hasBixbyComplianceFeatureFlag,
    hasPublicCompaniesFeatureFlag,
    profile: state.profileState.profile,
    hasUsEuroGroupingFeatureFlag,
  };
};

export default connect(mapStateToProps)(CompanyComps);
